import { BusinessNode, DummyRival, DummyRivalStatus, RNG } from '../domain/types.ts';

const DUMMY_STALL_NAMES = [
  'Trà Chanh Bụi Phố',
  'Nước Mía Siêu Sạch Cô Ba',
  'Cà Phê Bệt Sài Gòn',
  'Trà Tắc Khổng Lồ Chú Bảy',
  'Quán Cóc Tư Râu',
  'Sinh Tố Trái Cây Bà Tám',
  'Trà Đào Cam Sả Bé Lan',
  'Quán Nước Cây Đa Chém Gió',
  'Cà Phê Phin Phố Cổ',
  'Trà Bí Đao Hạt Chia',
  'Sấu Đá Hà Nội Bác An',
  'Rau Má Đậu Xanh Chị Hằng',
  'Quán Nước Mẹ Đốp',
  'Trà Trái Cây Nhiệt Đới',
  'Cà Phê Muối Chú Long',
];

const SPECIALTIES = [
  'Trà Chanh & Hướng Dương',
  'Nước Mía & Trái Cây Ướp Lạnh',
  'Cà Phê Sữa Đá & Bạc Xỉu',
  'Trà Đá Vỉa Hè & Kẹo Lạc',
  'Trà Tắc & Bò Khô Xé Cay',
  'Trà Đào Cam Sả & Hướng Dương',
  'Nước Sâm Dứa & Rau Má',
];

export class RivalSystem {
  /**
   * Tính toán doanh thu, lợi nhuận và phí sang quán theo mô hình kinh tế đường phố thực tế:
   * - Nếu quán tăng trưởng/ổn định: Phí sang quán >= 3 tháng lợi nhuận ròng của quán dummy.
   * - Nếu quán ế ẩm: Phí sang nhượng giảm sâu để vớt vát tiền cọc bàn ghế.
   * - Nếu quán vỡ nợ / phá sản: Sang lại 0 ĐỒNG (chủ cũ trốn nợ / giải cứu mặt bằng).
   */
  public static calculateRivalFinances(
    node: BusinessNode,
    status: DummyRivalStatus,
    level: number,
    rng: RNG
  ): { dailyRevenue: number; dailyProfit: number; monthlyProfit: number; transferFee: number } {
    // Doanh số ngày dựa trên lưu lượng người qua lại của node (trafficBase)
    const trafficFactor = Math.max(0.3, node.trafficBase);
    const variance = 0.85 + rng.next() * 0.3; // 0.85 - 1.15

    let baseDailyRev = Math.round(trafficFactor * 850_000 * variance * (1 + (level - 1) * 0.25));
    let profitMargin = 0.35; // Biên lợi nhuận ròng sau vốn và tiền thuê

    if (status === 'booming') {
      baseDailyRev = Math.round(baseDailyRev * 1.45);
      profitMargin = 0.42;
    } else if (status === 'stable') {
      baseDailyRev = Math.round(baseDailyRev * 1.0);
      profitMargin = 0.32;
    } else if (status === 'struggling') {
      baseDailyRev = Math.round(baseDailyRev * 0.55);
      profitMargin = 0.12;
    } else if (status === 'bankrupt') {
      baseDailyRev = Math.round(baseDailyRev * 0.25);
      profitMargin = -0.15; // Thua lỗ
    }

    const dailyRevenue = Math.max(50_000, baseDailyRev);
    const dailyProfit = Math.round(dailyRevenue * profitMargin - (node.rentPerDay || 30_000));
    const monthlyProfit = Math.max(0, dailyProfit * 30);

    // Tính tiền sang lại quán (Transfer Key Money):
    let transferFee = 0;
    if (status === 'booming') {
      // Tăng trưởng: Không dưới 3 tháng thu nhập của quán dummy tại vị trí đó
      transferFee = Math.max(node.unlockCost * 2, monthlyProfit * 3);
    } else if (status === 'stable') {
      // Ổn định: Tối thiểu 3 tháng lợi nhuận
      transferFee = Math.max(node.unlockCost * 1.5, monthlyProfit * 3);
    } else if (status === 'struggling') {
      // Ế ẩm: Sang rẻ thanh lý bàn ghế
      transferFee = Math.round(node.unlockCost * 0.35);
    } else if (status === 'bankrupt') {
      // Bán ế phá sản: Sang lại 0 ĐỒNG!
      transferFee = 0;
    }

    return {
      dailyRevenue,
      dailyProfit,
      monthlyProfit,
      transferFee: Math.round(transferFee),
    };
  }

  /**
   * Tạo dummy rivals ban đầu khi vừa nạp bản đồ khu vực
   */
  public static generateInitialRivals(
    nodes: BusinessNode[],
    playerOwnedNodeId: string | undefined,
    rng: RNG
  ): Record<string, DummyRival> {
    const rivals: Record<string, DummyRival> = {};
    const availableNodes = nodes.filter((n) => n.id !== playerOwnedNodeId);
    if (availableNodes.length <= 1) return rivals;

    // Chiếm đóng ban đầu 1 đến 2 vị trí ngẫu nhiên, luôn bảo đảm còn vị trí trống cho người mới
    const targetCount = Math.min(availableNodes.length - 1, availableNodes.length >= 4 ? 2 : 1);
    if (targetCount <= 0) return rivals;

    const shuffled = [...availableNodes].sort(() => rng.next() - 0.5);

    for (let i = 0; i < targetCount; i++) {
      const node = shuffled[i]!;
      const rival = this.createRivalForNode(node, 1, rng, i === 0 ? 'booming' : 'stable');
      rivals[node.id] = rival;
    }

    return rivals;
  }

  /**
   * Thuật toán xâm chiếm vỉa hè theo thời gian (khi kết thúc ngày ADVANCE_DAY):
   * - Các vị trí ($) còn trống dần bị các quán dummy thuật toán nhảy vào chiếm chỗ.
   * - Quán dummy hiện hữu biến chuyển tình hình làm ăn (phát đạt, ế ẩm, hoặc phá sản).
   */
  public static simulateRivalsTurn(
    currentRivals: Record<string, DummyRival>,
    nodes: BusinessNode[],
    playerOwnedNodeId: string | undefined,
    dayNumber: number,
    rng: RNG
  ): Record<string, DummyRival> {
    const nextRivals: Record<string, DummyRival> = {};

    // 1. Cập nhật tình trạng các quán dummy đang hoạt động
    for (const [nodeId, rival] of Object.entries(currentRivals)) {
      if (nodeId === playerOwnedNodeId) {
        // Người chơi đã sang lại quán này, dummy cũ rời đi
        continue;
      }

      const node = nodes.find((n) => n.id === nodeId);
      if (!node) continue;

      let nextStatus = rival.status;
      const roll = rng.next();

      // Tiến hóa tình trạng buôn bán
      if (rival.status === 'booming') {
        if (roll < 0.15) nextStatus = 'stable';
      } else if (rival.status === 'stable') {
        if (roll < 0.2) nextStatus = 'booming';
        else if (roll < 0.4) nextStatus = 'struggling';
      } else if (rival.status === 'struggling') {
        if (roll < 0.35) nextStatus = 'bankrupt'; // Chuyển sang vỡ nợ
        else if (roll < 0.55) nextStatus = 'stable';
      } else if (rival.status === 'bankrupt') {
        // Quán phá sản 0đ: nếu qua 2-3 ngày không ai sang lại, chủ mới nhảy vào gầy dựng lại
        if (roll < 0.5) nextStatus = 'struggling';
      }

      const finances = this.calculateRivalFinances(node, nextStatus, rival.level, rng);

      nextRivals[nodeId] = {
        ...rival,
        status: nextStatus,
        dailyRevenue: finances.dailyRevenue,
        dailyProfit: finances.dailyProfit,
        monthlyProfit: finances.monthlyProfit,
        transferFee: finances.transferFee,
        reputation: Math.max(10, Math.min(95, rival.reputation + (nextStatus === 'booming' ? 2 : nextStatus === 'bankrupt' ? -5 : 0))),
      };
    }

    // 2. Thuật toán dần xâm chiếm các vị trí ($) còn trống
    const vacantNodes = nodes.filter((n) => n.id !== playerOwnedNodeId && !nextRivals[n.id]);

    for (const node of vacantNodes) {
      // Tỷ lệ chiếm đóng mỗi ngày: 35% - 50% tùy theo độ đắc địa (trafficBase)
      const invasionChance = 0.25 + node.trafficBase * 0.35;
      if (rng.next() < invasionChance) {
        // Thuật toán sinh ra quán dummy mới nhảy vào mở quầy
        const initialStatus: DummyRivalStatus =
          node.trafficBase > 0.75
            ? (rng.next() < 0.6 ? 'booming' : 'stable')
            : (rng.next() < 0.4 ? 'stable' : rng.next() < 0.7 ? 'struggling' : 'bankrupt');

        const newRival = this.createRivalForNode(node, dayNumber, rng, initialStatus);
        nextRivals[node.id] = newRival;
      }
    }

    return nextRivals;
  }

  /**
   * Tính toán tác động cạnh tranh trực tiếp tới quán của người chơi:
   * Nếu có quán dummy nằm gần (trong bán kính 160m), họ sẽ hút bớt lượng khách vãng lai.
   */
  public static calculateRivalCompetitionPenalty(
    playerNodeId: string,
    nodes: BusinessNode[],
    rivals: Record<string, DummyRival>
  ): { trafficMultiplier: number; nearestRivalName: string | null } {
    const playerNode = nodes.find((n) => n.id === playerNodeId);
    if (!playerNode) return { trafficMultiplier: 1.0, nearestRivalName: null };

    let penalty = 0;
    let nearestRivalName: string | null = null;
    let closestDist = Infinity;

    for (const [nodeId, rival] of Object.entries(rivals)) {
      if (nodeId === playerNodeId) continue;
      const rNode = nodes.find((n) => n.id === nodeId);
      if (!rNode) continue;

      const dist = Math.hypot(rNode.x - playerNode.x, rNode.y - playerNode.y);
      if (dist < 180) {
        if (dist < closestDist) {
          closestDist = dist;
          nearestRivalName = rival.name;
        }

        // Cạnh tranh trực tiếp: Quán đang ăn nên làm ra hút khách mạnh hơn
        if (rival.status === 'booming') {
          penalty += 0.18;
        } else if (rival.status === 'stable') {
          penalty += 0.10;
        } else if (rival.status === 'struggling') {
          penalty += 0.04;
        }
      }
    }

    // Giới hạn giảm tối đa 35% khách do cạnh tranh
    const trafficMultiplier = Math.max(0.65, 1.0 - penalty);
    return { trafficMultiplier, nearestRivalName };
  }

  private static createRivalForNode(
    node: BusinessNode,
    dayNumber: number,
    rng: RNG,
    forcedStatus?: DummyRivalStatus
  ): DummyRival {
    const nameIndex = Math.floor(rng.next() * DUMMY_STALL_NAMES.length);
    const specIndex = Math.floor(rng.next() * SPECIALTIES.length);
    const level = 1 + Math.floor(rng.next() * 3); // Cấp 1 - 3

    const status: DummyRivalStatus = forcedStatus ?? (node.trafficBase > 0.7 ? 'booming' : 'stable');
    const finances = this.calculateRivalFinances(node, status, level, rng);

    return {
      id: `dummy-${node.id}-${Date.now().toString(36)}`,
      nodeId: node.id,
      name: DUMMY_STALL_NAMES[nameIndex]!,
      level,
      reputation: Math.round(35 + rng.next() * 45),
      status,
      dailyRevenue: finances.dailyRevenue,
      dailyProfit: finances.dailyProfit,
      monthlyProfit: finances.monthlyProfit,
      transferFee: finances.transferFee,
      specialty: SPECIALTIES[specIndex]!,
      establishedDay: dayNumber,
    };
  }
}
